import Mustache from 'mustache';
import { debounce } from './helpers';

/**
 * Content filtering module:
 *
 * - Autosubmits filter form on any child field change
 * - Fetches new data
 * - Renders data to container with Mustache
 * - Updates total number of results with support for plurality
 * - Updates loadMore button url (see module load-more.js)
 *
 * Data attributes "more-btn", "total-results" and "additional-templates" are optional.
 *
 * Usage:
 * <form
 *   method="get"
 *   action=""
 *   class="js--content-filtering"
 *   data-ajax-url="URL_TO_QUERY"
 *   data-target-container="RESULT_CONTAINER_ID"
 *   data-template="MUSTACHE_TEMPLATE_ID"
 *   data-additional-templates='{"OBJECT_PROPERTY": "MUSTACHE_TEMPLATE_ID"}'
 *   data-more-btn="LOAD_MORE_BUTTON_ID"
 *   data-total-results="TOTAL_RESULTS_CONTAINER_ID"
 * >
 *   some inputs
 * </form>
 * <div id="RESULT_CONTAINER_ID">results will be added here</div>
 * <script id="MUSTACHE_TEMPLATE_ID" type="x-tmpl-mustache">List item template {{someData}}</script>
 * <button id="LOAD_MORE_BUTTON_ID" class="load-more js--load-more" data-ajax-url="URL_TO_QUERY" data-target-container="RESULT_CONTAINER_ID" data-template="MUSTACHE_TEMPLATE_ID">Load more</button>
 * <span ID="TOTAL_RESULTS_CONTAINER_ID" data-single="item" data-plural="items"></span>
 */
export class ContentFiltering {
  #processing = false;
  #form = null;
  #ajaxUrl = null;
  #resultContainer = null;
  #template = '';
  #additionalTemplates = {};
  #moreBtn = null;
  #totalResultsContainer = null;

  constructor(formEl) {

    if(formEl.dataset.ajaxUrl) {
      this.#ajaxUrl = formEl.dataset.ajaxUrl;
    } else {
      throw new Error('ContentFiltering: ajaxUrl missing');
    }

    if(formEl.dataset.targetContainer) {
      const resultContainerElement = document.getElementById(formEl.dataset.targetContainer);
      if(resultContainerElement) {
        this.#resultContainer = resultContainerElement;
      } else {
        throw new Error('ContentFiltering: target container element not found');
      }
    } else {
      throw new Error('ContentFiltering: data-target-container missing');
    }

    if(formEl.dataset.template) {
      const templateElement = document.getElementById(formEl.dataset.template);
      if(templateElement) {
        this.#template = templateElement.innerHTML;
      } else {
        throw new Error('ContentFiltering: templateElement not found');
      }
    } else {
      throw new Error('ContentFiltering: data-template missing');
    }

    // Optional additional templates
    if(formEl.dataset.additionalTemplates) {
      try {
        const templates = JSON.parse(formEl.dataset.additionalTemplates);

        Object.keys(templates).forEach(prop => {
          const templateId = templates[prop];
          const templateEl = document.getElementById(templateId);

          if(templateEl) {
            this.#additionalTemplates[prop] = templateEl.innerHTML;
          }
        });
      } catch(e) {
        // Do nothing
      }
    }

    if(formEl.dataset.moreBtn) {
      // Optional, but you wont get pagination without this.
      const moreBtnElement = document.getElementById(formEl.dataset.moreBtn);
      if(moreBtnElement) {
        this.#moreBtn = moreBtnElement;
      }
    }

    if(formEl.dataset.totalResults) {
      // Optional
      const totalResultsContainer = document.getElementById(formEl.dataset.totalResults);
      if(totalResultsContainer) {
        this.#totalResultsContainer = totalResultsContainer;
      }
    }

    // Attach event listeners
    formEl.addEventListener('change', this.onFormChange.bind(this));
    formEl.addEventListener('submit', this.onFormSubmit.bind(this));

    this.#form = formEl;
    this.debouncedTrigger = debounce(this.triggerSubmit.bind(this), 250);
  }

  /**
   * Called when any of form fields is changed.
   * Debounces neccessary fields and autosubmits form.
   *
   * @param {Event} event - Form change event
   * @memberof ContentFiltering
   */
  onFormChange(event) {
    if(!this.#processing) {
      if(event.target.tagName === 'INPUT' && event.target.type === 'date') {
        // Debounce so people have time to write
        this.debouncedTrigger();
      } else {
        // Selects can trigger immediately
        this.triggerSubmit();
      }
    }
  }

  /**
   * Triggers submit event on the form
   *
   * @memberof ContentFiltering
   */
  triggerSubmit() {
    this.#form.dispatchEvent(new Event('submit', {bubbles: false, cancelable: true}));
  }


  /**
   * Submits form using ajax, disables form controls during request,
   * Updates results to container
   *
   * @param {Event} event - Form submit event
   * @memberof ContentFiltering
   */
  onFormSubmit(event) {
    event.preventDefault();

    if(!this.#processing) {
      // Get form values
      const formData = this.getFormData();

      // Set status
      this.toggleProcessing(true);

      // Empty current results
      this.#resultContainer.replaceChildren();

      // Hide more btn
      if(this.#moreBtn) {
        this.#moreBtn.classList.add('hidden');
      }

      // Convert form data to query params
      const query = this.serialize(formData);
      const url = this.#ajaxUrl + (this.#ajaxUrl.indexOf('?') === -1 ? '?' : '&') + query;

      fetch(url)
        .then(response => {
          if(response.ok) {
            return response.json();
          } else {
            throw new Error(response.statusText);
          }
        })
        .then(data => {
          // Add new items to list
          let markup = '';

          data.items.forEach(item => {
            // Default item template
            let itemTemplate = this.#template;

            // If item has a template prop and additional
            // template markup is found, use that for the item
            if(item.template && this.#additionalTemplates[item.template]) {
              itemTemplate = this.#additionalTemplates[item.template];
            }

            markup += Mustache.render(itemTemplate, item);
          });

          this.#resultContainer.insertAdjacentHTML('beforeend', markup);

          // Update total number
          this.updateTotalResults(data.total);

          // Update more btn
          if(data.nextPage && this.#moreBtn) {
            this.#moreBtn.dataset.ajaxUrl = data.nextPage;
            this.#moreBtn.classList.remove('hidden');
          }
        })
        .catch(error => {
          console.log(error);

          this.updateTotalResults(0);
        })
        .finally(() => {
          // Reset status
          this.toggleProcessing(false);
        });
    }
  }

  /**
   * Returns form data as object
   *
   * @returns {Object} - Form data
   */
  getFormData() {
    const formData = new FormData(this.#form);
    const formDataObj = {};

    for (let [key, value] of formData.entries()) {
      if(key.endsWith('[]')) {
        // Make into array
        const fieldName = key.replace('[]', '');
        if(typeof formDataObj[fieldName] === 'undefined') {
          formDataObj[fieldName] = [];
        }

        formDataObj[fieldName].push(value);
      } else {
        formDataObj[key] = value;
      }
    }

    return formDataObj;
  }

  /**
   * Serializes an object to query param string.
   * Supports arrays.
   *
   * @sample: serialize({foo: 'bar', baz: [1, 2]}) -> &foo=bar&baz[]=1&baz[]=2
   *
   * @param {Object} data - Data to serialize
   * @returns {string}
   */
  serialize(data = {}) {
    const query = Object.keys(data).map(key => {
      if(Array.isArray(data[key])) {
        // Make array into &key[]=value1&key[]=value2
        return data[key].map(arrValue => encodeURIComponent(key) + '[]=' + encodeURIComponent(arrValue)).join('&');
      } else {
        return encodeURIComponent(key) + '=' + encodeURIComponent(data[key]);
      }
    }).join('&');

    return query;
  }

  /**
   * Toggles form processing status.
   * If true, disables all form fields and adds `processing` class to form for visual styling purposes
   * If false, opposite of ^
   *
   * @param {boolean} status - Processing or not
   */
  toggleProcessing(status) {
    this.#processing = status;
    this.#form.classList.toggle('processing', status);
    Array.from(this.#form.elements).forEach(formElement => formElement.disabled = status);
  }

  /**
   * Updates total results number, if containing element exists.
   * Supports plural/single forms.
   * Inserts '1 result' or '2 results' if translations are set. If not, inserts just the number.
   *
   * @param {number} total - Number of total results
   */
  updateTotalResults(total = 0) {
    if(this.#totalResultsContainer) {
      const mode = total === 1 ? 'single' : 'plural';
      const types = Array.from(this.#form.elements).map(el => { return el.checked ? el.getAttribute('data-label') : null }).filter(Boolean)
      const typesTxt = types.length > 0 ? `${this.#totalResultsContainer.dataset['types']} ${types.join(', ')}` : ''
      const resultTxt = `${total} ${this.#totalResultsContainer.dataset[mode] || ''} ${typesTxt}`;

      this.#totalResultsContainer.innerText = resultTxt;
    }
  }

}

export default {
  init: function() {
    document.querySelectorAll('.js--content-filtering').forEach(form => {
      new ContentFiltering(form);
    });
  }
};
